import { useState, useEffect, useCallback } from 'react';
import { useAuth } from "../../context/AuthProvider";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from 'sonner';
import Loading from '../../cors/Loading';
import ProductImageGallery from "../../components/ProductImageGallery";
import VariantSelector from "../../components/VariantSelector";
import OrderModal from '../../cors/OrderModal'; 
import { BASE_URL } from '../../apiConfig';
import Cookies from 'js-cookie';
import { ShoppingCart } from 'lucide-react';

export default function ProductDetail() {
  const { token, role } = useAuth();
  const { id } = useParams();
  const router = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState([]); 
  const [isModalOpenOrder, setIsModalOpenOrder] = useState(false); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProduct = useCallback(async () => {
    if (!id) {
      setError("Product ID is missing. Please provide a valid product ID.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (!token) {
        toast.error("You are not authenticated. Please log in to view product details.");
        router('/login');
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/products/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `Failed to fetch product: ${res.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.status === true && data.data) {
        setProduct(data.data);
        setSelectedVariants([{}]);
      } else {
        throw new Error(data.message || "Invalid data format from API or product not found.");
      }
    } catch (err) {
      console.error("Error fetching product:", err);
      setError(err.message || "An unexpected error occurred while fetching the product details.");
      toast.error(`Error: ${err.message || "Failed to load product."}`);
    } finally {
      setLoading(false);
    }
  }, [id]);



  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  useEffect(() => {
    if (quantity > selectedVariants.length) {
      const newVariants = [...selectedVariants];
      while (newVariants.length < quantity) {
        newVariants.push({});
      }
      setSelectedVariants(newVariants);
    } else if (quantity < selectedVariants.length) {
      setSelectedVariants(selectedVariants.slice(0, quantity));
    }
  }, [quantity]);

  const handleVariantChange = (variants, index) => {
    const newSelectedVariants = [...selectedVariants];
    newSelectedVariants[index] = variants;
    setSelectedVariants(newSelectedVariants);
  };

const handleAddToCart = () => {
  if (!product) return;

  if (product?.variants?.length > 0) {
    const requiredVariantTypes = Array.from(
      new Set(
        product.variants.flatMap(group => 
          group.details.map(detail => detail.variant.type)
        )
      )
    );

    for (let i = 0; i < quantity; i++) {
      const missingTypes = requiredVariantTypes.filter(
        type => !selectedVariants[i]?.[type]
      );

      if (missingTypes.length > 0) {
        const missingTypesText = missingTypes.map(t => `"${t}"`).join(', ');
        toast.error(
          `Unit ${i + 1}: Please select all required variants. Missing: ${missingTypesText}`,
          { duration: 5000 }
        );
        
        missingTypes.forEach(type => {
          const element = document.querySelector(`[data-variant-type="${type}-${i}"]`);
          element?.classList.add('animate-pulse', 'ring-2', 'ring-red-500');
          setTimeout(() => {
            element?.classList.remove('animate-pulse', 'ring-2', 'ring-red-500');
          }, 3000);
        });
        return;
      }

      const isValidCombination = product.variants.some(group =>
        group.details.every(detail =>
          selectedVariants[i][detail.variant.type]?.id === detail.variant.id
        )
      );

      if (!isValidCombination) {
        toast.error(`Unit ${i + 1}: Selected variant combination is not available`, { duration: 5000 });
        return;
      }
    }
  }

  const cartKey = "cart";
  const existingCart = JSON.parse(Cookies.get(cartKey) || "[]");

  if (!product?.variants?.length) {
    const existingIndex = existingCart.findIndex(item => item.productId === product.id);
    
    if (existingIndex !== -1) {
      existingCart[existingIndex].quantity += quantity;
    } else {
      existingCart.push({
        key: `${product.id}`,
        productId: product.id,
        name: product.name,
        quantity,
        price: isDiscounted ? discountedPrice : originalPrice,
        sellingPrice: product.price?.selling_price,
        selectedVariants: {},
        image: product.image_py || (product.images?.[0]?.image || null),
      });
    }
  } 
  // For products with variants
  else {
    // Group selected variants by their combination
    const variantGroups = {};
    
    selectedVariants.forEach(variants => {
      const variantKey = Object.keys(variants)
        .sort()
        .map(type => `${type}:${variants[type].id}`)
        .join('|');
      
      if (!variantGroups[variantKey]) {
        variantGroups[variantKey] = {
          variants,
          count: 0
        };
      }
      variantGroups[variantKey].count++;
    });

    // Process each variant group
    Object.entries(variantGroups).forEach(([variantKey, { variants, count }]) => {
      const cartItemKey = `${product.id}-${variantKey}`;
      const existingIndex = existingCart.findIndex(item => item.key === cartItemKey);

      if (existingIndex !== -1) {
        // If this variant combination already exists, just increase its quantity
        existingCart[existingIndex].quantity += count;
      } else {
        // Otherwise add new item with this variant combination
        existingCart.push({
          key: cartItemKey,
          productId: product.id,
          name: product.name,
          quantity: count,
          price: isDiscounted ? discountedPrice : originalPrice,
          sellingPrice: product.price?.selling_price,
          selectedVariants: variants,
          image: product.image_py || (product.images?.[0]?.image || null),
        });
      }
    });
  }

  const expires = new Date();
  expires.setMinutes(expires.getMinutes() + 70); 

  Cookies.set(cartKey, JSON.stringify(existingCart), { 
    expires: expires,
    path: '/',
  });
  
  toast.success(`${quantity} x ${product.name} added to cart!`);
};

  if (loading) {
    return <div className='flex justify-center my-72'><Loading /></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-red-600">Error</h2>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <button
            onClick={() => fetchProduct()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[var(--color-primary-500)] hover:bg-[#089c87] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-primary-500)]"
          >
            Try Again
          </button>
          <button
            onClick={() => router("/dashboard")}
            className="ml-4 mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-[var(--color-primary-500)] bg-white border-[var(--color-primary-500)] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-primary-500)]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Product Not Found</h2>
          <p className="mt-2 text-sm text-gray-600">The product you are looking for does not exist or is unavailable.</p>
          <button
            onClick={() => router('/products')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[var(--color-primary-500)] hover:bg-[#089c87] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-primary-500)]"
          >
            Browse Other Products
          </button>
        </div>
      </div>
    );
  }

  const originalPrice = parseFloat(product.price?.saller_price || 0);
  const now = new Date();
  const isDiscounted = product.discount && 
                      product.discount.status === true && // status should be boolean true
                      new Date(product.discount.date_start) <= now && 
                      new Date(product.discount.date_end) >= now;

  const discountedPrice = isDiscounted
    ? originalPrice - (originalPrice * (product.discount.value || 0)) / 100
    : originalPrice;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
          <div className="lg:sticky lg:top-8">
            <ProductImageGallery
              images={[
                ...(product.image_py ? [{ id: -1, product_id: product.id, image: product.image_py }] : []),
                ...(product.images || [])
              ]}
              productName={product.name}
            />
          </div>

          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-center space-x-2">
                {product.category?.title && (
                  <span className="rounded-full bg-[var(--color-primary-50)] px-3 py-1 text-xs font-medium text-[var(--color-primary-500)]">
                    {product.category.title}
                  </span>
                )}
                {product.status === "active" ? (
                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                    In Stock
                  </span>
                ) : (
                  <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                    Out of Stock
                  </span>
                )}
                {product.product_expired_soon === true && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-[10px] font-semibold">STOCK LIMITE</span>
                )}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 sm:text-3xl mt-5">{product.name}</h1>
              <div className='flex items-center text-sm gap-1'>
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={i < product.rating ? 'text-yellow-500 text-lg' : 'text-gray-300 text-lg'}>
                    ★
                  </span>
                ))}
                ({product.rating}/5)
              </div>
              <div className='text-gray-500 text-xs'>PRODUCT ID : {product.id}</div>
            </div>

            <div className="border-b border-gray-200 pb-8">
              <div className="flex itmes-end gap-3">
                <span className="text-2xl font-bold text-green-600">{discountedPrice.toFixed(2)}MAD</span>
                {isDiscounted && (
                  <div className='flex items-center gap-2 '>
                    <span className="text-gray-500 line-through">{originalPrice.toFixed(2)}MAD</span>
                    <span className="rounded-lg bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                      -{product.discount.value || 0}% OFF
                    </span>
                  </div>
                )}
              </div>
              {isDiscounted && product.discount?.date_end && (
                <p className="my-2 text-xs text-gray-600">
                  Offre valable jusqu'au {new Date(product.discount.date_end).toLocaleDateString('en-GB')}
                </p>
              )}
              <span className="text-sm text-gray-500 mt-4">Recommandé a vente : <span className='font-bold'>{product.price?.selling_price}MAD</span></span>
            </div>

            {product.description && (
              <div className="border-b border-gray-200 pb-8">
                <h3 className="font-medium text-gray-900 mb-4">Description</h3>
                <p className="text-gray-700 leading-relaxed text-sm">{product.description}</p>
              </div>
            )}

            {product.image_urls && (
              <div className="border-b border-gray-200 pb-8">
                <h3 className="font-medium text-gray-900 mb-1">Autre images : </h3>
                <Link
                  to={product.image_urls}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline text-sm"
                >
                  {product.image_urls}
                </Link>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label htmlFor="quantity" className="text-sm font-medium text-gray-900 mb-3 block">
                  Quantity
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                      className="p-2 cursor-pointer hover:bg-gray-100 transition-colors duration-200 rounded-l-lg"
                      disabled={quantity <= 1}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <span className="px-4 py-2 text-center min-w-[60px] border-l border-r border-gray-300">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(prev => Math.min(product.price.stock, prev + 1))}
                      className="p-2 cursor-pointer hover:bg-gray-100 transition-colors duration-200 rounded-r-lg"
                      disabled={quantity >= (product.price?.stock || 0)}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Variant selectors for each unit */}
              {product.variants && product.variants.length > 0 && (
                <div className="border-b border-gray-200 pb-8 space-y-4">
                  <h3 className="font-medium text-gray-900">Select Variants</h3>
                  {Array.from({ length: quantity }).map((_, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg px-4 py-2">
                      <h4 className="font-medium mb-1">Article #{index + 1}</h4>
                      <VariantSelector
                        variants={product.variants}
                        onVariantChange={(variants) => handleVariantChange(variants, index)}
                        initialSelectedVariants={selectedVariants[index] || {}}
                        index={index} // Pass index to create unique data attributes
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={() => setIsModalOpenOrder(true)} 
                  className={`flex-1 text-sm px-8 py-3 rounded-xl font-semibold text-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
                    ${product.status === 'active' && (product.price?.stock || 0) > 0
                      ? 'bg-[var(--color-primary-500)] text-white cursor-pointer hover:bg-[var(--color-primary-400)] focus:ring-[var(--color-primary-500)]'
                      : 'bg-gray-300 text-white cursor-not-allowed focus:ring-gray-300'}
                  `}
                  disabled={(product.status !== 'active') || (product.price?.stock || 0) <= 0}
                >
                  {product.status === 'active' && (product.price?.stock || 0) > 0 ? 'Commandez' : 'Out of Stock'}
                </button>

                <button
                  onClick={handleAddToCart}
                  className={`border px-8 py-3 rounded-xl font-semibold text-lg transition-colors duration-200 text-xs
                    ${product.status === 'active' && (product.price?.stock || 0) > 0
                      ? 'border-[var(--color-primary-500)] text-[var(--color-primary-500)] hover:bg-[var(--color-primary-500)] hover:text-white cursor-pointer'
                      : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'}
                  `}
                  disabled={(product.status !== 'active') || (product.price?.stock || 0) <= 0}
                >
                  {product.status === 'active' && (product.price?.stock || 0) > 0
                    ? <ShoppingCart size={20} />
                    : 'Out of Stock'}
                </button>
              </div>
            </div>
            {
              role === 1 ?
                <div className=" space-y-6">
                  <div>
                    <div className='border-t border-gray-200 py-8'>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Product Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        {product.fournisseur && (
                          <>
                            <div>
                              <span className="font-medium text-gray-900">Fournisseur:</span>
                              <span className="ml-2 text-gray-700">{product.fournisseur.name}({product.fournisseur.name_personnel})</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">Contact:</span>
                              <span className="ml-2 text-gray-700">{product.fournisseur.phone}</span>
                            </div>
                          </>
                        )}
                        <div>
                          <span className="font-medium text-gray-900">Prix d'achat:</span>
                          <span className="ml-2 text-gray-700">{product.price.achat_price} MAD</span>
                        </div>
                        {product.price?.stock !== undefined && (
                          <div>
                            <span className="font-medium text-gray-900">Stock:</span>
                            <span className="ml-2 text-gray-700">{product.price.stock} units</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {product.sallers && product.sallers.length > 0 && (
                        <div className='border-y border-gray-200 py-8'>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Vendeurs :</h3>
                          <div className=" flex items-center gap-3">
                            {product.sallers.map((saller,id) => (
                              <label
                                key={id}
                                className={`max-w-max flex items-center py-1.5 px-2 rounded-full cursor-pointer transition-all duration-200 ${
                                  "bg-[var(--color-primary-50)] border-2 border-[var(--color-primary-500)]"
                                }`}
                              >
                                <div className="">
                                  <div className="text-xs font-medium text-[var(--color-primary-500)]">
                                    {saller.saller_info?.name} {saller.saller_info?.name_store && `(${saller.saller_info.name_store})`}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              :''
            }
          </div>
        </div>
      </div>
      {
        isModalOpenOrder &&
          <OrderModal
            isOpen={isModalOpenOrder}
            onClose={() => setIsModalOpenOrder(false)}
            product={product}
          />
      }
    </div>
  );
}
